import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Flag, Buildings, Package, ChatCircle, ShieldCheck, GraduationCap, DownloadSimple,
  Question, ArrowLeft, ArrowSquareOut, Sparkle,
} from "@phosphor-icons/react";

const ICONS = {
  flag: Flag, buildings: Buildings, package: Package, chat: ChatCircle,
  shield: ShieldCheck, graduation: GraduationCap, download: DownloadSimple, question: Question,
};

export default function RepTraining() {
  const [data, setData] = useState(null);
  const [activeKey, setActiveKey] = useState("start_here");
  const idxRef = useRef(0);

  const load = () => api.get("/portal/training").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  const toggle = async (item, done) => {
    setData((d) => ({ ...d, progress: { ...d.progress, [item]: done } }));
    try { await api.post("/portal/training/progress", { item, done }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); load(); }
  };

  if (!data) return <div className="flex h-screen items-center justify-center font-mono text-sm text-muted-foreground">Loading…</div>;

  const section = data.sections.find((s) => s.key === activeKey) || data.sections[0];
  const progress = data.progress || {};
  idxRef.current = 0; // reset checkbox counter for this render

  const mdComponents = {
    input: ({ node, ...props }) => {
      if (props.type === "checkbox") {
        const key = `${section.key}:${idxRef.current++}`;
        const checked = !!progress[key];
        return (
          <input type="checkbox" checked={checked} onChange={() => toggle(key, !checked)}
            data-testid={`training-check-${key}`}
            className="mr-2 mt-1 h-4 w-4 shrink-0 cursor-pointer rounded accent-[#3ab795]" />
        );
      }
      return <input {...props} />;
    },
    a: ({ node, ...props }) => <a {...props} target="_blank" rel="noreferrer" className="text-[#3ab795] underline underline-offset-2" />,
  };

  return (
    <div className="min-h-screen bg-background" data-testid="rep-training-page">
      <header className="sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-[#102542] text-white"><GraduationCap size={20} weight="fill" /></div>
            <div>
              <div className="font-heading text-lg font-bold leading-tight">Training</div>
              <div className="text-xs text-muted-foreground">Your enablement library — read top to bottom</div>
            </div>
          </div>
          <Link to="/portal" data-testid="training-back-btn" className="inline-flex items-center gap-1.5 rounded-sm border border-border px-3 py-1.5 text-sm hover:bg-accent transition-colors">
            <ArrowLeft size={15} /> Back to portal
          </Link>
        </div>
      </header>

      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-6 py-8 md:grid-cols-[240px_1fr]">
        <nav className="h-max md:sticky md:top-24" data-testid="training-nav">
          <ul className="space-y-1">
            {data.sections.map((s) => {
              const Icon = ICONS[s.icon] || Flag;
              const active = s.key === activeKey;
              return (
                <li key={s.key}>
                  <button data-testid={`training-nav-${s.key}`} onClick={() => setActiveKey(s.key)}
                    className={`flex w-full items-center gap-2.5 rounded-sm px-3 py-2 text-left text-sm transition-colors ${active ? "bg-[#102542] text-white" : "hover:bg-accent"}`}>
                    <Icon size={17} weight={active ? "fill" : "regular"} /> {s.title}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <main className="min-w-0" data-testid="training-content">
          <h1 className="mb-5 font-heading text-3xl font-black tracking-tight">{section.title}</h1>

          {section.key === "grohub" && (
            <div className="mb-6 rounded-sm border border-[#3ab795]/40 bg-[#3ab795]/10 p-5" data-testid="platform-cta">
              <div className="mb-1 flex items-center gap-2 font-heading text-base font-semibold"><Sparkle size={18} weight="fill" className="text-[#3ab795]" /> Your Platform — LocalPro OS</div>
              <p className="mb-1 text-sm text-muted-foreground">Your CRM, AI receptionist, calendar, and platform tools live here.</p>
              {data.votel_login_url ? (
                <a href={data.votel_login_url} target="_blank" rel="noreferrer" data-testid="platform-open-btn"
                  className="mt-2 inline-flex items-center gap-2 rounded-sm bg-[#102542] px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity">
                  Access your CRM and platform tools <ArrowSquareOut size={15} />
                </a>
              ) : (
                <p className="mt-1 text-sm text-muted-foreground" data-testid="platform-coming-soon">Coming soon — ask your admin for your login link.</p>
              )}
            </div>
          )}

          {section.key === "assets" ? (
            <AssetsManifest assets={data.assets} intro={section.body_md} components={mdComponents} />
          ) : (
            <article className="training-prose max-w-none text-[15px] leading-relaxed">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{section.body_md}</ReactMarkdown>
            </article>
          )}

          <p className="mt-10 border-t border-border pt-4 text-xs italic text-muted-foreground" data-testid="training-footer">{data.footer}</p>
        </main>
      </div>
    </div>
  );
}

function AssetsManifest({ assets, intro, components }) {
  const cats = ["Core Kit", "Outreach Templates", "Leave-Behinds", "Reference"];
  const grouped = cats.map((c) => [c, assets.filter((a) => a.category === c)]).filter(([, a]) => a.length);
  return (
    <div>
      <article className="training-prose mb-6 max-w-none text-[15px] leading-relaxed">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{(intro || "").split("## ")[0]}</ReactMarkdown>
      </article>
      {grouped.map(([cat, items]) => (
        <div key={cat} className="mb-6">
          <h2 className="mb-3 overline">{cat}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {items.map((a) => (
              <div key={a.id} data-testid={`asset-${a.id}`} className="rounded-sm border border-border bg-card p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium">{a.title}</div>
                  {a.per_rep_merge && <span className="rounded-full bg-[#f5a623]/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#b9770f]">Personalized</span>}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{a.description}</p>
                {a.file_url ? (
                  <a href={a.file_url} target="_blank" rel="noreferrer" data-testid={`asset-download-${a.id}`}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-sm bg-[#102542] px-3 py-1.5 text-sm text-white hover:opacity-90 transition-opacity">
                    <DownloadSimple size={15} /> Download
                  </a>
                ) : (
                  <div className="mt-3 inline-block rounded-sm border border-dashed border-border px-3 py-1.5 text-xs text-muted-foreground">Coming soon</div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
